import {testIdProps} from '@github-ui/test-id-props'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {FormControl} from '@primer/react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useId, useState} from 'react'

import type {ExtendedRepository} from '../api/common-contracts'
import {apiSearchRepositories} from '../api/repository/api-search-repositories'
import {RepositoryPicker} from '../forms/repository-picker/repository-picker'
import {useRepositories} from '../state-providers/repositories/use-repositories'
import {DefaultIssueCreateRepositorySectionResources} from '../strings'
import styles from './default-issue-create-repository-section.module.css'

export function DefaultIssueCreateRepositorySection({
  defaultIssueCreateRepository,
  onRepositoryChange,
  noResultsMessage,
  noFilterResultsMessage,
}: {
  /** User's already-configured default repository */
  defaultIssueCreateRepository?: ExtendedRepository | null
  /** Form state change function */
  onRepositoryChange: (repository: ExtendedRepository | null) => void
  /** Custom message to display when no repositories are available */
  noResultsMessage?: {title: string; body: string}
  /** Custom message to display when filtering returns no results */
  noFilterResultsMessage?: {title: string; body: string}
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const onOpenChange = useCallback((open: boolean) => {
    if (open) {
      setIsOpen(true)
      setQuery('')
    } else {
      setIsOpen(false)
      setQuery('')
    }
  }, [])
  const [selectedRepository, setSelectedRepository] = useState<ExtendedRepository | null>(
    defaultIssueCreateRepository ?? null,
  )

  const {suggestRepositories} = useRepositories()
  const queryClient = useQueryClient()

  const normalizedQuery = query.trim()

  const {
    data: repositories = [],
    error,
    isError,
    isLoading,
  } = useQuery(
    {
      queryKey: ['default-issue-repo-suggestions', normalizedQuery, isOpen],
      queryFn: async () => {
        if (!isOpen) return []

        if (normalizedQuery === '') {
          return suggestRepositories()
        } else {
          const {repositories: searchedRepos} = await apiSearchRepositories({query: normalizedQuery})
          return searchedRepos
        }
      },
      staleTime: 5 * 60 * 1000, // cache for 5 minutes
    },
    queryClient,
  )

  useEffect(() => {
    if (defaultIssueCreateRepository) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRepository(defaultIssueCreateRepository)
    } else if (defaultIssueCreateRepository === null) {
      setSelectedRepository(null)
    }
  }, [defaultIssueCreateRepository])

  const updateSelectedRepository = useCallback(
    (repo: ExtendedRepository | null) => {
      setSelectedRepository(repo)
      onRepositoryChange(repo)
    },
    [onRepositoryChange],
  )

  const getRepository = useCallback((repoId: number) => repositories.find(repo => repo.id === repoId), [repositories])

  const onSearchChange = (value: string) => {
    setQuery(value)
  }

  const onSelectedChange = useCallback(
    (selected?: ItemInput) => {
      if (!selected?.id) return

      const repo = getRepository(selected.id as number)
      const isCurrentlySelected = selectedRepository?.id === selected.id

      if (repo) {
        // Toggle selection - if already selected, unselect it
        if (isCurrentlySelected) {
          updateSelectedRepository(null)
        } else {
          updateSelectedRepository(repo)
        }
      }
    },
    [getRepository, selectedRepository, updateSelectedRepository],
  )

  // Prepare custom messages if provided
  const customNoResultsMessage = noResultsMessage ? {variant: 'empty' as const, ...noResultsMessage} : undefined

  const customNoFilterResultsMessage = noFilterResultsMessage
    ? {variant: 'empty' as const, ...noFilterResultsMessage}
    : undefined

  const anchorId = useId()
  const labelId = useId()
  const descriptionId = useId()

  return (
    <FormControl className={styles.FormControl}>
      <FormControl.Label id={labelId}>{DefaultIssueCreateRepositorySectionResources.sectionTitle}</FormControl.Label>

      <RepositoryPicker
        selectedRepository={selectedRepository as ExtendedRepository}
        repositories={repositories}
        query={query}
        loading={isLoading}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onSearchChange={onSearchChange}
        onSelectedChange={onSelectedChange}
        testIdPrefix="default-repository-picker"
        noResultsMessage={customNoResultsMessage}
        noFilterResultsMessage={customNoFilterResultsMessage}
        anchorProps={{
          id: anchorId,
          // The repository picker uses a combination of the visual label text, plus the current value inside of the button
          // as the accessible name for the button. We could use a custom `aria-label` here, but that would be redundant,
          // as this ensures that screen readers get the same information as what is visually displayed.
          'aria-labelledby': `${labelId} ${anchorId}`,
          'aria-describedby': descriptionId,
        }}
      />

      <FormControl.Caption id={descriptionId}>
        {DefaultIssueCreateRepositorySectionResources.sectionCaption}
      </FormControl.Caption>

      {isError && (
        <FormControl.Validation variant="error" {...testIdProps('default-repository-picker-error-message')}>
          {error instanceof Error ? error.message : 'An error occurred'}
        </FormControl.Validation>
      )}
    </FormControl>
  )
}
